/**
 * Admin Platform GearSix
 * Global platform configuration and feature flags
 */
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { adminApiGet, adminApiPatch } from '../../utils/adminApi';
import toast from '../../utils/toast';

import {
  CheckCircle,
  CircleNotch,
  Envelope,
  FloppyDisk,
  GearSix as SettingsIcon,
  Globe,
  Image,
  ToggleLeft,
  ToggleRight,
  Warning
} from '@phosphor-icons/react';
const PlatformSettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    branding: {
      platform_name: '',
      logo_url: '',
      favicon_url: '',
      primary_color: '#6366F1',
    },
    emails: {
      from_email: '',
      enquiry_email: '',
      support_email: '',
      reply_to_email: '',
    },
    urls: {
      marketing_site: '',
      app_url: '',
      help_center: '',
    },
    features: {
      enable_voice_agents: true,
      enable_job_descriptions: true,
      enable_reports: true,
      enable_blogs: true,
    },
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const data = await adminApiGet('/api/admin/settings/platform');
      setSettings(data.settings || settings);
    } catch (error) {
      console.error('Error fetching settings:', error);
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await adminApiPatch('/api/admin/settings/platform', settings);
      toast.success('Settings saved successfully');
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error(error.message || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (section, field, value) => {
    setSettings(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value,
      },
    }));
  };

  const toggleFeature = (feature) => {
    setSettings(prev => ({
      ...prev,
      features: {
        ...prev.features,
        [feature]: !prev.features[feature],
      },
    }));
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-12 bg-white/10 rounded animate-pulse w-64"></div>
        <div className="space-y-4">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="h-48 bg-white/10 rounded-xl animate-pulse"></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">Platform Settings</h1>
          <p className="text-gray-400">Configure global platform settings and features</p>
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className="px-4 py-2 bg-gradient-to-r from-[#6366F1] to-[#EC4899] rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center gap-2 font-semibold"
        >
          {saving ? (
            <>
              <CircleNotch size={18} className="animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <FloppyDisk size={18} />
              Save Changes
            </>
          )}
        </button>
      </div>

      {/* Branding Section */}
      <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
            <Image size={20} className="text-purple-400" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-white">Branding</h2>
            <p className="text-sm text-gray-400">Platform name, logo, and visual identity</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Platform Name</label>
            <input
              type="text"
              value={settings.branding.platform_name}
              onChange={(e) => handleChange('branding', 'platform_name', e.target.value)}
              placeholder="Man & Machine"
              className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#6366F1]/50"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Primary Brand Color</label>
            <div className="flex gap-2">
              <input
                type="color"
                value={settings.branding.primary_color}
                onChange={(e) => handleChange('branding', 'primary_color', e.target.value)}
                className="w-16 h-10 bg-white/5 border border-white/10 rounded-lg cursor-pointer"
              />
              <input
                type="text"
                value={settings.branding.primary_color}
                onChange={(e) => handleChange('branding', 'primary_color', e.target.value)}
                placeholder="#6366F1"
                className="flex-1 px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#6366F1]/50"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Logo URL</label>
            <input
              type="text"
              value={settings.branding.logo_url}
              onChange={(e) => handleChange('branding', 'logo_url', e.target.value)}
              placeholder="https://example.com/logo.png"
              className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#6366F1]/50"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Favicon URL</label>
            <input
              type="text"
              value={settings.branding.favicon_url}
              onChange={(e) => handleChange('branding', 'favicon_url', e.target.value)}
              placeholder="https://example.com/favicon.ico"
              className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#6366F1]/50"
            />
          </div>
        </div>
      </div>

      {/* Email Section */}
      <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
            <Envelope size={20} className="text-blue-400" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-white">Email Configuration</h2>
            <p className="text-sm text-gray-400">Email addresses for system communications</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">From Email</label>
            <input
              type="email"
              value={settings.emails.from_email}
              onChange={(e) => handleChange('emails', 'from_email', e.target.value)}
              placeholder="noreply@example.com"
              className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#6366F1]/50"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Reply-To Email</label>
            <input
              type="email"
              value={settings.emails.reply_to_email}
              onChange={(e) => handleChange('emails', 'reply_to_email', e.target.value)}
              placeholder="hello@example.com"
              className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#6366F1]/50"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Enquiry Email</label>
            <input
              type="email"
              value={settings.emails.enquiry_email}
              onChange={(e) => handleChange('emails', 'enquiry_email', e.target.value)}
              placeholder="enquiries@example.com"
              className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#6366F1]/50"
            />
            <p className="text-xs text-gray-500 mt-1">Contact form submissions will be sent here</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Support Email</label>
            <input
              type="email"
              value={settings.emails.support_email}
              onChange={(e) => handleChange('emails', 'support_email', e.target.value)}
              placeholder="support@example.com"
              className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#6366F1]/50"
            />
            <p className="text-xs text-gray-500 mt-1">Support requests will be sent here</p>
          </div>
        </div>
      </div>

      {/* URLs Section */}
      <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
            <Globe size={20} className="text-green-400" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-white">URLs</h2>
            <p className="text-sm text-gray-400">Platform and external URLs</p>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Marketing Site URL</label>
            <input
              type="url"
              value={settings.urls.marketing_site}
              onChange={(e) => handleChange('urls', 'marketing_site', e.target.value)}
              placeholder="https://www.example.com"
              className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#6366F1]/50"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">App URL</label>
            <input
              type="url"
              value={settings.urls.app_url}
              onChange={(e) => handleChange('urls', 'app_url', e.target.value)}
              placeholder="https://app.example.com"
              className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#6366F1]/50"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Help Center / Docs URL</label>
            <input
              type="url"
              value={settings.urls.help_center}
              onChange={(e) => handleChange('urls', 'help_center', e.target.value)}
              placeholder="https://help.example.com"
              className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#6366F1]/50"
            />
          </div>
        </div>
      </div>

      {/* Feature Flags Section */}
      <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-lg bg-yellow-500/20 flex items-center justify-center">
            <SettingsIcon size={20} className="text-yellow-400" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-white">Feature Flags</h2>
            <p className="text-sm text-gray-400">Enable or disable platform features</p>
          </div>
        </div>

        <div className="space-y-4">
          {[
            { key: 'enable_voice_agents', label: 'Voice Agents', desc: 'Enable AI voice agent creation and management' },
            { key: 'enable_job_descriptions', label: 'Job Descriptions', desc: 'Enable AI-powered job description generation' },
            { key: 'enable_reports', label: 'Reports', desc: 'Enable analytics and reporting features' },
            { key: 'enable_blogs', label: 'Blogs', desc: 'Enable blog functionality on marketing site' },
          ].map((feature) => (
            <div key={feature.key} className="flex items-center justify-between p-4 bg-white/5 rounded-lg">
              <div>
                <p className="font-medium text-white">{feature.label}</p>
                <p className="text-sm text-gray-400">{feature.desc}</p>
              </div>
              <button
                onClick={() => toggleFeature(feature.key)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  settings.features[feature.key] ? 'bg-[#6366F1]' : 'bg-white/20'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    settings.features[feature.key] ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Danger Zone */}
      <div className="bg-red-500/5 backdrop-blur-sm border border-red-500/20 rounded-xl p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-lg bg-red-500/20 flex items-center justify-center">
            <Warning size={20} className="text-red-400" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-white">Danger Zone</h2>
            <p className="text-sm text-gray-400">Sensitive operations - use with caution</p>
          </div>
        </div>

        <div className="space-y-3">
          <button
            disabled
            className="w-full px-4 py-3 bg-red-500/10 border border-red-500/30 rounded-lg text-left opacity-50 cursor-not-allowed"
          >
            <p className="font-medium text-red-400">Flush Cached Analytics</p>
            <p className="text-sm text-red-300/60">Clear all cached analytics data (TODO)</p>
          </button>
          <button
            disabled
            className="w-full px-4 py-3 bg-red-500/10 border border-red-500/30 rounded-lg text-left opacity-50 cursor-not-allowed"
          >
            <p className="font-medium text-red-400">Reset Demo Tenant</p>
            <p className="text-sm text-red-300/60">Reset demo organization to initial state (TODO)</p>
          </button>
        </div>
      </div>
    </div>
  );
};

export default PlatformSettings;
